import { env } from 'core/config/environment';
import { isParsableJson } from 'core/utils/isParsableJson';
import { useEffect } from 'react';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ToastType, useToast } from 'services/toast';

// This code was take from the Twilio documentation
// https://www.twilio.com/docs/whatsapp/tech-provider-program/integration-guide#part-3-technical-integration

type Params = {
  onSuccess: ({
    whatsAppBusinessAccountId
  }: {
    whatsAppBusinessAccountId: string;
  }) => void;
};

export const useFacebookLogin = ({ onSuccess }: Params) => {
  const { showToast } = useToast();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();

  useEffect(() => {
    const embeddedSignupInfoListener = async (event: any) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        if (!isParsableJson(event.data)) {
          return;
        }

        const data = JSON.parse(event.data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // if user finishes the Embedded signup flow
          if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
            showLoadingOverlay();
            const { waba_id } = data.data;

            await onSuccess({ whatsAppBusinessAccountId: waba_id });

            showToast({
              title: 'WhatsApp channel being set up',
              message: "You'll receive an email when this is complete",
              type: ToastType.Success
            });

            hideLoadingOverlay();
          } else if (data.event === 'CANCEL') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { current_step } = data.data;
            // TODO: Handle user cancellation if needed
          } else if (data.event === 'ERROR') {
            const { error_message } = data.data;
            showToast({
              message: error_message,
              type: ToastType.Error
            });
          }
        }
      } catch {
        showToast({
          message: 'Something went wrong',
          type: ToastType.Error
        });

        hideLoadingOverlay();
      }
    };

    window.addEventListener('message', embeddedSignupInfoListener);

    return () => {
      window.removeEventListener('message', embeddedSignupInfoListener);
    };
  }, [hideLoadingOverlay, showLoadingOverlay, showToast]);

  const launchEmbeddedSignup = ({
    useSystemPhoneNumber,
    organizationName,
    websiteUrl,
    phoneNumberWithoutCountryCode,
    countryCode,
    email
  }: {
    useSystemPhoneNumber: boolean;
    organizationName?: string;
    websiteUrl?: string;
    phoneNumberWithoutCountryCode?: string;
    countryCode?: number;
    email?: string;
  }) => {
    window.FB.login(
      function () {
        // Since we are using Twilio's APIs, we do not need to do anything with the response here.
      },
      {
        config_id: env.FACEBOOK_LOGIN_CONFIG_ID,
        auth_type: 'rerequest',
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: 3, // Required to get WABA ID
          // set the following "featureType" to 'only_waba_sharing'
          // if and only if using a Twilio SMS-capable number, otherwise
          // do not include it or set it to null
          featureType: useSystemPhoneNumber ? 'only_waba_sharing' : null,
          setup: {
            solutionID: env.TWILIO_PARTNER_SOLUTION_ID,
            business: {
              name: organizationName,
              website: websiteUrl,
              email: email,
              phone: {
                code: 1,
                number: '6505559999'
              }
            },
            phone: {
              code: countryCode,
              number: phoneNumberWithoutCountryCode,
              displayName: organizationName,
              category: 'PROF_SERVICES'
            }
          }
        }
      }
    );
  };

  return { launchEmbeddedSignup };
};
